import type { CustomField, Media, Product, Variant } from "@spree/sdk";

/**
 * Google Merchant Center product feed.
 *
 * Title and description come from the product's `google.title` /
 * `google.description` metafields (Spree admin → Settings → Metafield
 * Definitions, display_on "both" so the Store API returns them), falling back
 * to the product name / description when unset. A market can override them
 * with a suffixed field, e.g. `google.title_us` / `_uk` / `_eu` on those feeds
 * (see marketCustomFieldValue). Only the feed uses them — the
 * product page and its JSON-LD keep the on-page name and description, which
 * Google requires structured data to match.
 *
 * Price, availability and GTIN always come from Spree so the feed matches the
 * landing page.
 *
 * @see https://support.google.com/merchants/answer/7052112 (product data spec)
 */

export const GOOGLE_TITLE_KEY = "google.title";
export const GOOGLE_DESCRIPTION_KEY = "google.description";
/** The manufacturer brand printed on the pack (e.g. "World A.B.S"). */
export const GOOGLE_BRAND_KEY = "google.brand";

/** Google's hard limits for `title` and `description`. */
const TITLE_MAX = 150;
const DESCRIPTION_MAX = 5000;

/** `barcode` is added to Store API variants by the backend (Tkf::Api::V3::VariantSerializer). */
export type VariantWithBarcode = Variant & { barcode?: string | null };

export type MerchantAvailability =
  | "in_stock"
  | "out_of_stock"
  | "backorder"
  | "preorder";

export interface MerchantItem {
  id: string;
  title: string;
  description: string;
  link: string;
  imageLink?: string;
  additionalImageLinks: string[];
  availability: MerchantAvailability;
  availabilityDate?: string;
  /** Formatted as Google expects: "75.00 GBP". */
  price: string;
  brand: string;
  condition: "new";
  gtin?: string;
  mpn?: string;
  /** "no" only when there's no GTIN at all, so Google doesn't demand one. */
  identifierExists?: "no";
  itemGroupId?: string;
}

/**
 * Whether a custom field is feed-only data (`google.*` namespace). These must be
 * storefront-visible for the Store API to return them, so the product page has
 * to filter them out itself.
 */
export function isGoogleFeedField(field: Pick<CustomField, "key">): boolean {
  return field.key.startsWith("google.");
}

/** Trimmed string value of a custom field, or undefined when blank/missing. */
export function customFieldValue(
  fields: CustomField[] | undefined,
  key: string,
): string | undefined {
  const value = fields?.find((f) => f.key === key)?.value;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

/**
 * The metafield suffix for a storefront domain: its TLD, so `tongkatfitness.us`
 * → `us`, `.uk` → `uk`, `.eu` → `eu`. The Rest-of-World `.com` has no suffix
 * and uses the shared fields. Env prefixes (`stg.`) don't affect the TLD.
 *
 * Deliberately not the market's Spree country: `.uk` resolves as `gb` and `.eu`
 * as `de`, which aren't the names editors use for these fields.
 */
export function marketFieldSuffix(
  host: string | null | undefined,
): string | undefined {
  const tld = host?.toLowerCase().split(":")[0].split(".").at(-1);
  return tld && tld !== "com" ? tld : undefined;
}

/**
 * A market-specific custom field (`google.title_uk`) for the feed's market,
 * falling back to the shared field (`google.title`). See marketFieldSuffix for
 * the suffix each domain uses.
 */
export function marketCustomFieldValue(
  fields: CustomField[] | undefined,
  key: string,
  market?: string,
): string | undefined {
  const marketValue = market
    ? customFieldValue(fields, `${key}_${market.toLowerCase()}`)
    : undefined;
  return marketValue ?? customFieldValue(fields, key);
}

/**
 * The barcode as a GTIN if it's a valid GTIN-8/12/13/14 (length + GS1 check
 * digit), otherwise undefined. Spaces and dashes are ignored.
 */
export function normalizeGtin(
  barcode: string | null | undefined,
): string | undefined {
  const digits = barcode?.replace(/[\s-]/g, "");
  if (!digits || !/^\d+$/.test(digits)) return undefined;
  if (![8, 12, 13, 14].includes(digits.length)) return undefined;

  const body = digits.slice(0, -1);
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    // Weights alternate 3,1,3… starting from the digit next to the check digit.
    const weight = (body.length - i) % 2 === 1 ? 3 : 1;
    sum += Number(body[i]) * weight;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === Number(digits.at(-1)) ? digits : undefined;
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/** Collapse whitespace so multi-line descriptions become one clean paragraph block. */
function plainText(html: string): string {
  // Not seo.ts's stripHtml: seo.ts imports this module, so that would be circular.
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .trim();
}

function imageUrls(media: Media[] | undefined): string[] {
  return (media ?? [])
    .map((m) => m.original_url || m.large_url)
    .filter((url): url is string => Boolean(url));
}

function availabilityFor(item: {
  in_stock: boolean;
  backorderable: boolean;
  preorder?: boolean;
}): MerchantAvailability {
  if (item.preorder) return "preorder";
  if (item.in_stock) return "in_stock";
  if (item.backorderable) return "backorder";
  return "out_of_stock";
}

/**
 * The product's brand: its `google.brand` metafield, else `fallback` (the store
 * name). Used by both the feed and the Product JSON-LD so Google sees one brand,
 * and it must match the pack — Google checks brand against the GTIN.
 */
export function productBrand(
  product: Pick<Product, "custom_fields">,
  fallback: string,
): string {
  return customFieldValue(product.custom_fields, GOOGLE_BRAND_KEY) ?? fallback;
}

/**
 * Build feed items for one product: one item per variant for multi-variant
 * products (grouped by `item_group_id`), otherwise one item from the default
 * variant. Items without a price are skipped — Google rejects them.
 */
export function buildMerchantItems(
  product: Product & { preorder?: boolean; preorder_ships_at?: string | null },
  origin: string,
  defaultBrand: string,
  market?: string,
): MerchantItem[] {
  const brand = productBrand(product, defaultBrand);
  const base = origin.replace(/\/$/, "");
  const link = `${base}/products/${product.slug}`;

  const title =
    marketCustomFieldValue(product.custom_fields, GOOGLE_TITLE_KEY, market) ??
    product.name;
  const description =
    marketCustomFieldValue(
      product.custom_fields,
      GOOGLE_DESCRIPTION_KEY,
      market,
    ) ?? plainText(product.description || product.description_html || "");

  const productImages = imageUrls(product.media);
  if (productImages.length === 0 && product.thumbnail_url) {
    productImages.push(product.thumbnail_url);
  }

  const variants = (product.variants ?? []) as VariantWithBarcode[];
  const sources: VariantWithBarcode[] =
    variants.length > 0
      ? variants
      : product.default_variant
        ? [product.default_variant as VariantWithBarcode]
        : [];
  const grouped = variants.length > 1;

  const items: MerchantItem[] = [];
  for (const variant of sources) {
    const price = variant.price ?? product.price;
    const amount = Number(price?.amount);
    if (!price?.currency || !Number.isFinite(amount) || amount <= 0) continue;

    const variantImages = imageUrls(variant.media);
    const images = variantImages.length > 0 ? variantImages : productImages;
    const gtin = normalizeGtin(variant.barcode);
    const preorder = grouped
      ? (variant as Variant & { preorder?: boolean }).preorder
      : product.preorder;
    const shipsAt = grouped
      ? (variant as Variant & { preorder_ships_at?: string | null })
          .preorder_ships_at
      : product.preorder_ships_at;

    const itemTitle =
      grouped && variant.options_text
        ? `${title} – ${variant.options_text}`
        : title;

    items.push({
      id: variant.sku || variant.id,
      title: truncate(itemTitle, TITLE_MAX),
      description: truncate(description || title, DESCRIPTION_MAX),
      link: grouped ? `${link}?variant=${variant.id}` : link,
      imageLink: images[0],
      additionalImageLinks: images.slice(1, 11),
      availability: availabilityFor({
        in_stock: variant.in_stock,
        backorderable: variant.backorderable,
        preorder,
      }),
      ...(preorder && shipsAt ? { availabilityDate: shipsAt } : {}),
      price: `${amount.toFixed(2)} ${price.currency}`,
      brand,
      condition: "new",
      ...(gtin ? { gtin } : {}),
      ...(variant.sku ? { mpn: variant.sku } : {}),
      // Only when Spree says the barcode is blank. A missing key means an older
      // backend that doesn't serialize it — don't tell Google there's no GTIN.
      ...("barcode" in variant && !variant.barcode?.trim()
        ? { identifierExists: "no" as const }
        : {}),
      ...(grouped ? { itemGroupId: product.id } : {}),
    });
  }
  return items;
}

export function escapeXml(value: string): string {
  return (
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;")
      // Strip characters that are illegal in XML 1.0.
      // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional
      .replace(/[ --]/g, "")
  );
}

function tag(name: string, value: string | undefined): string {
  return value ? `<${name}>${escapeXml(value)}</${name}>` : "";
}

/** Render items as a Google Merchant RSS 2.0 feed. */
export function renderMerchantFeedXml(channel: {
  title: string;
  link: string;
  description: string;
  items: MerchantItem[];
}): string {
  const items = channel.items
    .map((item) =>
      [
        "<item>",
        tag("g:id", item.id),
        tag("g:title", item.title),
        tag("g:description", item.description),
        tag("g:link", item.link),
        tag("g:image_link", item.imageLink),
        ...item.additionalImageLinks.map((url) =>
          tag("g:additional_image_link", url),
        ),
        tag("g:availability", item.availability),
        tag("g:availability_date", item.availabilityDate),
        tag("g:price", item.price),
        tag("g:brand", item.brand),
        tag("g:condition", item.condition),
        tag("g:gtin", item.gtin),
        tag("g:mpn", item.mpn),
        tag("g:identifier_exists", item.identifierExists),
        tag("g:item_group_id", item.itemGroupId),
        "</item>",
      ]
        .filter(Boolean)
        .join(""),
    )
    .join("\n");

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<rss version="2.0" xmlns:g="http://base.google.com/ns/1.0">',
    "<channel>",
    tag("title", channel.title),
    tag("link", channel.link),
    tag("description", channel.description),
    items,
    "</channel>",
    "</rss>",
    "",
  ].join("\n");
}
